// src/pages/profile-settings/patient/Allergies.jsx
import React from "react";
import {
  Search as SearchIcon,
  Plus,
  CircleX as CircleXIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import supabase from "@/services/supabaseClient";
import { useAuth } from "@/contexts/AuthContext.jsx";
import Card from "@/components/ui/Card.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import SaveChip from "@/sot/primitives/SaveChip.jsx";
import Divider from "@/components/ui/Divider.jsx";
import Checkbox from "@/components/ui/Checkbox.jsx";
import TapButton from "@/pages/onboarding/_shared/TapButton.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import { loadAllergies } from "@/utils/allergies";
import { shouldHideUntranslatedCatalog } from "@/utils/i18nGuardrails.js";
import { PATIENT_SETTINGS_PROFILE_ROUTE } from "@/pages/profile-settings/settingsNavigation.js";
import FolaOnboardingCard from "@/pages/profile-settings/FolaOnboardingCard.jsx";
import {
  loadPatientIntroPreview,
  savePatientIntroPreview,
} from "@/lib/patientIntroPreview";

const COMPLETION_SAFE_ALLERGIES = ["None", "Other", "Don't know"];

const FRENCH_SAFE_ALLERGIES = {
  None: "Aucune",
  Other: "Autre",
  "Don't know": "Je ne sais pas",
};

function isFrenchLanguageCode(languageCode) {
  return String(languageCode || "").toLowerCase().startsWith("fr");
}

function getAllergyDisplayLabel(value, languageCode) {
  const text = String(value || "").trim();
  if (!isFrenchLanguageCode(languageCode)) return text;
  return FRENCH_SAFE_ALLERGIES[text] || text;
}

/**
 * Patient Settings → Allergies
 */
export default function AllergiesSettings() {
  const nav = useNavigate();
  const { refreshProfile } = useAuth() || {};
  const { t, languageCode } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);

  // Seed from intro preview (string → array)
  const introPreview = React.useMemo(
    () => loadPatientIntroPreview() || {},
    []
  );

  const initialSelected = React.useMemo(() => {
    const raw = introPreview.allergies;
    if (Array.isArray(raw)) {
      return raw.map((item) => String(item || "").trim()).filter(Boolean);
    }
    if (typeof raw !== "string") return [];
    const normalized = raw.trim();
    if (!normalized) return [];
    return normalized
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }, [introPreview]);

  const initialKey = React.useMemo(
    () => JSON.stringify([...initialSelected].sort()),
    [initialSelected]
  );

  const [all, setAll] = React.useState([]);
  const [query, setQuery] = React.useState("");
  const [seedSelected, setSeedSelected] = React.useState(initialSelected);
  const [localSelected, setLocalSelected] = React.useState(initialSelected);
  const [showConfirmLeave, setShowConfirmLeave] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");

  // keep local in sync if seed changes (rare)
  React.useEffect(() => {
    setSeedSelected(initialSelected);
    setLocalSelected(initialSelected);
  }, [initialKey]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateFromDbIfNeeded() {
      if (initialSelected.length > 0) return;

      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();
      if (!alive || authErr || !user?.id) return;

      const { data: row, error: rowErr } = await supabase
        .from("patient_profiles")
        .select("allergies")
        .eq("user_id", user.id)
        .maybeSingle();
      if (!alive || rowErr || !row) return;

      const parsed = Array.isArray(row.allergies)
        ? row.allergies
        : String(row.allergies || "")
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);

      const deduped = Array.from(
        new Map(parsed.map((item) => [String(item).toLowerCase(), String(item)])).values()
      );
      if (!deduped.length) return;

      setSeedSelected(deduped);
      setLocalSelected(deduped);
      savePatientIntroPreview({
        ...(loadPatientIntroPreview() || {}),
        allergies: deduped,
      });
    }

    hydrateFromDbIfNeeded();
    return () => {
      alive = false;
    };
  }, [initialSelected.length]);

  // load allergies once
  React.useEffect(() => {
    let ok = true;
    (async () => {
      const names = await loadAllergies();
      if (ok) setAll(names);
    })();
    return () => {
      ok = false;
    };
  }, []);

  const q = query.trim().toLowerCase();
  const options = React.useMemo(() => {
    const seen = new Set();
    const next = [];
    for (const option of [...COMPLETION_SAFE_ALLERGIES, ...all, ...seedSelected, ...localSelected]) {
      const canonical = String(option || "").trim();
      const key = canonical.toLowerCase();
      if (!canonical || seen.has(key)) continue;
      seen.add(key);
      next.push(canonical);
    }
    return next;
  }, [all, localSelected, seedSelected]);
  const filtered = q ? options.filter((n) => n.toLowerCase().includes(q)) : options;
  const hideCatalog = isFrench || shouldHideUntranslatedCatalog(languageCode);
  const visibleOptions = hideCatalog
    ? filtered.filter(
        (item) =>
          COMPLETION_SAFE_ALLERGIES.includes(item) ||
          seedSelected.includes(item) ||
          localSelected.includes(item)
      )
    : filtered;

  const seedKey = React.useMemo(
    () => JSON.stringify([...seedSelected].slice().sort()),
    [seedSelected]
  );

  const sortedLocalKey = React.useMemo(
    () => JSON.stringify([...localSelected].slice().sort()),
    [localSelected]
  );
  const dirty = sortedLocalKey !== seedKey;
  const canSave = dirty && localSelected.length > 0;

  const commit = (next) => {
    const dedup = Array.from(
      new Map(next.map((n) => [n.toLowerCase(), n])).values()
    );
    setLocalSelected(dedup);
  };

  const toggle = (name) => {
    const isSpecial = COMPLETION_SAFE_ALLERGIES.includes(name);
    setLocalSelected((prev) => {
      let next;
      if (prev.includes(name)) {
        next = prev.filter((x) => x !== name);
      } else if (isSpecial) {
        next = [name];
      } else {
        next = [
          ...prev.filter((item) => !COMPLETION_SAFE_ALLERGIES.includes(item)),
          name,
        ];
      }
      return next;
    });
  };

  const addCustom = () => {
    const raw = query.trim();
    if (!raw) return;

    const pretty = raw.replace(/\s+/g, " ").trim();
    if (!all.some((n) => n.toLowerCase() === pretty.toLowerCase())) {
      setAll((prev) => [pretty, ...prev]);
    }
    if (
      !localSelected.some(
        (n) => n.toLowerCase() === pretty.toLowerCase()
      )
    ) {
      commit([
        pretty,
        ...localSelected.filter(
          (item) => !COMPLETION_SAFE_ALLERGIES.includes(item)
        ),
      ]);
    }
    setQuery("");
  };

  const addCustomRef = React.useRef(addCustom);
  React.useEffect(() => {
    addCustomRef.current = addCustom;
  }, [addCustom]);

  const handlePlusClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addCustomRef.current();
  };

  async function handleSave() {
    if (!canSave) return;

    try {
      const current = loadPatientIntroPreview() || {};
      const next = {
        ...current,
        allergies: localSelected.slice(),
      };
      savePatientIntroPreview(next);

      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();

      if (!authErr && user?.id) {
        const { error: upErr } = await supabase
          .from("patient_profiles")
          .upsert(
            {
              user_id: user.id,
              allergies: localSelected.slice(),
            },
            { onConflict: "user_id" }
          );

        if (upErr) throw upErr;

        try {
          await refreshProfile?.();
        } catch {
          // non-fatal
        }
      }

      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        nav(PATIENT_SETTINGS_PROFILE_ROUTE);
      }, 2200);
    } catch (err) {
      console.error("[AllergiesSettings] Save error:", err);
      setSaveError(
        t(
          "settings.subflows.allergies.saveError",
          isFrench ? "Impossible d’enregistrer les modifications. Veuillez réessayer." : "Could not save changes. Please try again."
        )
      );
    }
  }

  function handleClose() {
    if (!dirty) {
      nav(PATIENT_SETTINGS_PROFILE_ROUTE);
    } else {
      setShowConfirmLeave(true);
    }
  }

  return (
    <SettingsScreenShell>
      <TopBarCenter
        title={t("settings.subflows.allergies.title", isFrench ? "Allergies" : "Allergies")}
        onBack={handleClose}
        close
        right={<SaveChip disabled={!canSave} onClick={handleSave} />}
      />

      <div className="mt-3">
        <FolaOnboardingCard role="patient" />
      </div>

      {/* Search row */}
      <div className="mt-3 flex items-center gap-3">
        <SearchIcon
          className="h-5 w-5 text-[rgb(var(--clr-on-surface-variant,86,106,139))]"
          aria-hidden
        />

        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCustom();
            }
          }}
          placeholder={t(
            "settings.subflows.allergies.searchPlaceholder",
            isFrench ? "Rechercher ou ajouter" : "Search or add"
          )}
          inputMode="search"
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="off"
          className="flex-1 bg-transparent outline-none border-0 focus:outline-none focus:ring-0
                     text-[16px] leading-6 placeholder:text-[rgb(var(--clr-on-surface-variant,86,106,139))]"
        />

        {!!q && (
          <TapButton
            ariaLabel={t("settings.subflows.allergies.clearSearch", isFrench ? "Effacer la recherche" : "Clear search")}
            onTap={() => setQuery("")}
            className="shrink-0 grid place-items-center"
          >
            <CircleXIcon className="h-5 w-5 text-[rgb(var(--clr-on-surface-variant,86,106,139))]" />
          </TapButton>
        )}

        <button
          type="button"
          onMouseDown={handlePlusClick}
          onTouchStart={handlePlusClick}
          onClick={handlePlusClick}
          className="shrink-0 grid place-items-center rounded-full
                     w-8 h-8 border border-[rgba(24,50,119,0.16)]
                     hover:bg-[rgba(24,50,119,0.04)]
                     focus:outline-none focus:ring-2 focus:ring-[rgba(24,50,119,0.24)]"
          aria-label={t("settings.subflows.allergies.addAria", isFrench ? "Ajouter une allergie" : "Add allergy")}
        >
          <Plus className="h-4 w-4 text-[rgb(var(--clr-on-surface-variant,86,106,139))]" />
        </button>
      </div>

      <Divider variant="outline" full className="mt-4 mb-6" />

      {/* List card – rounded 12px */}
      <Card className="relative z-10 overflow-hidden">
        {visibleOptions.map((name, idx) => {
          const checked = localSelected.includes(name);
          return (
            <div key={`${name}-${idx}`}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggle(name)}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") && toggle(name)
                }
                className="w-full flex items-center"
                style={{
                  WebkitTapHighlightColor: "transparent",
                  touchAction: "manipulation",
                }}
              >
                <div className="flex-1 pl-5 pr-4 py-4 text-left">
                  <span className="text-body-large text-[rgb(var(--clr-on-surface,4,8,18))]">
                    {getAllergyDisplayLabel(name, languageCode)}
                  </span>
                </div>
                <div className="mr-4">
                  <Checkbox
                    checked={checked}
                    onChange={(next) => {
                      if (next !== checked) toggle(name);
                    }}
                  />
                </div>
              </div>
              {idx < visibleOptions.length - 1 && (
                <Divider inset variant="outlineVariant" />
              )}
            </div>
          );
        })}

        {visibleOptions.length === 0 && (
          <div className="px-5 py-6 text-body-medium text-on-surface-var">
            {hideCatalog && !q
              ? t(
                  "settings.subflows.allergies.catalogHidden",
                  isFrench
                    ? "Saisissez une allergie et appuyez sur + pour l’ajouter. Les suggestions non traduites sont masquées dans cette langue."
                    : "Type an allergy and tap + to add it. Untranslated suggestions are hidden on this screen in this language.",
                )
              : t(
                  "settings.subflows.allergies.noMatchesAdd",
                  isFrench ? "Aucun résultat. Appuyez sur + pour ajouter « {query} »." : "No matches. Tap + to add “{query}”.",
                  { query: query.trim() },
                )}
          </div>
        )}
      </Card>

      {/* Unsaved-changes confirm */}
      {showConfirmLeave && (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <div
      className="absolute inset-0 bg-black/30"
      onClick={() => setShowConfirmLeave(false)}
    />

    <div
      className="relative w-[312px] max-w-[560px] min-w-[288px]
                 bg-[var(--Schemes-Surface,#FFFFFF)] rounded-[28px] overflow-hidden
                 shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.06),
                         0px_6px_12px_-4px_rgba(29,79,215,0.06),
                         0px_3px_6px_-2px_rgba(26,27,35,0.06),
                         0px_3px_3px_-1px_rgba(26,27,35,0.06),
                         0px_1.5px_0px_-0.5px_rgba(26,27,35,0.06),
                         0px_0px_0px_1px_rgba(24,50,119,0.10)]"
    >
      <div className="px-6 pt-6">
        <div className="text-headline-small text-on-surface">
          {t("settings.subflows.discardTitle", isFrench ? "Abandonner les modifications ?" : "Discard changes?")}
        </div>

        <div className="text-body-medium text-on-surface-var mt-4">
          {t(
            "settings.subflows.allergies.discardBody",
            isFrench
              ? "Vous avez des modifications d’allergies non enregistrées. Voulez-vous fermer sans enregistrer ?"
              : "You have unsaved allergy changes. Are you sure you want to close without saving?"
          )}
        </div>
      </div>

      <div className="p-4 flex gap-3">
        <button
          className="flex-1 rounded-xl px-6 py-3 text-label-large whitespace-nowrap"
          style={{
            background:
              "color-mix(in oklab, var(--Schemes-On-Surface,#040812) 8%, transparent)",
            color: "rgb(var(--afrimed-primary))",
          }}
          onClick={() => setShowConfirmLeave(false)}
        >
          {t("common.keepEditing", isFrench ? "Continuer la modification" : "Keep editing")}
        </button>

        <button
          className="flex-1 rounded-xl px-6 py-3 text-label-large whitespace-nowrap"
          style={{
            background:
              "color-mix(in oklab, var(--Schemes-Critical,#F8533A) 12%, transparent)",
            color: "var(--Schemes-Critical,#F8533A)",
          }}
          onClick={() => {
            setShowConfirmLeave(false);
            nav(PATIENT_SETTINGS_PROFILE_ROUTE);
          }}
        >
          {t("common.discard", isFrench ? "Abandonner" : "Discard")}
        </button>
      </div>
    </div>
  </div>
)}

{/* Global “Changes saved” Snackbar */}
      <Snackbar open={saved} onClose={() => setSaved(false)} duration={2200}>
        {t("common.changesSaved", isFrench ? "Modifications enregistrées" : "Changes saved")}
      </Snackbar>

      {saveError && (
        <Snackbar
          open={Boolean(saveError)}
          onClose={() => setSaveError("")}
          duration={2800}
          variant="error"
        >
          {saveError || t("settings.subflows.allergies.saveError", isFrench ? "Impossible d’enregistrer les modifications. Veuillez réessayer." : "Could not save changes. Please try again.")}
        </Snackbar>
      )}
    </SettingsScreenShell>
  );
}
